import axios, {
  RawAxiosRequestHeaders,
  InternalAxiosRequestConfig,
  AxiosError,
  AxiosResponse,
} from 'axios'
import { Cookies } from 'react-cookie'

import { apiUrl, cookieOptions, PHONE_VERIFICATION_REQUIRED_MESSAGE } from './constants'

const API = axios.create({
  baseURL: apiUrl,
})

let isRefreshing = false
let failedQueue: Array<{
  resolve: (value: string) => void
  reject: (error: any) => void
}> = []

/**
 * Process all queued requests after token refresh
 */
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else if (token) {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

/**
 * Clear authentication tokens from cookies
 */
const clearAuthTokens = () => {
  const cookies = new Cookies()

  cookies.remove('accessToken', { path: '/' })
  cookies.remove('refreshToken', { path: '/' })
}

/**
 * Store new authentication tokens in cookies
 */
const storeAuthTokens = (accessToken: string, refreshToken: string) => {
  const cookies = new Cookies()

  cookies.set('accessToken', accessToken, cookieOptions)
  cookies.set('refreshToken', refreshToken, cookieOptions)
}

/**
 * Queue request to be retried after token refresh
 */
const queueFailedRequest = (): Promise<any> => {
  return new Promise((resolve, reject) => {
    failedQueue.push({ resolve, reject })
  })
}

/**
 * Redirect user to login and clear tokens
 */
const redirectToLogin = () => {
  clearAuthTokens()
  window.location.href = '/login'
}

/**
 * Attempt to refresh the access token using refresh token
 */
const refreshAccessToken = async (refreshToken: string) => {
  const response = await axios.post(`${apiUrl}/refresh`, { refreshToken })

  return response.data
}

/**
 * Handle token refresh process
 */
const handleTokenRefresh = async (originalRequest: any, error: AxiosError) => {
  const cookies = new Cookies()
  const refreshToken = cookies.get('refreshToken')

  if (!refreshToken) {
    redirectToLogin()

    return Promise.reject(error)
  }

  try {
    const { accessToken, refreshToken: newRefreshToken } = await refreshAccessToken(refreshToken)

    // Store new tokens
    storeAuthTokens(accessToken, newRefreshToken)

    // Update the authorization header for the original request
    originalRequest.headers.Authorization = `Bearer ${accessToken}`

    // Process all queued requests with the new token
    processQueue(null, accessToken)

    isRefreshing = false

    // Retry the original request
    return API(originalRequest)
  } catch (refreshError) {
    // Refresh failed, log out user
    processQueue(refreshError, null)
    redirectToLogin()
    isRefreshing = false

    return Promise.reject(refreshError)
  }
}

/**
 * Handle 401 Unauthorized responses with token refresh
 */
const handle401Error = async (error: AxiosError) => {
  const originalRequest: any = error.config
  const responseData: any = error.response?.data

  // Check if 401 is due to phone verification requirement - let it pass through
  if (
    error.response?.status === 401 &&
    responseData?.message === PHONE_VERIFICATION_REQUIRED_MESSAGE
  ) {
    return Promise.reject(error)
  }

  // If error is 401 and we haven't retried yet
  if (error.response?.status === 401 && !originalRequest._retry) {
    // If already refreshing, queue this request
    if (isRefreshing) {
      return queueFailedRequest().then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`

        return API(originalRequest)
      })
    }

    originalRequest._retry = true
    isRefreshing = true

    return handleTokenRefresh(originalRequest, error)
  }

  return Promise.reject(error)
}

// Request interceptor: Add access token to headers
API.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const cookies = new Cookies()
    const token = cookies.get('accessToken')

    if (token) {
      config.headers = config.headers ?? {}
      ;(config.headers as RawAxiosRequestHeaders).Authorization = `Bearer ${token}`
    }

    return config
  },
  (error) => Promise.reject(error),
)

// Response interceptor: Handle 401 errors with token refresh
API.interceptors.response.use((response: AxiosResponse) => response, handle401Error)

export { API, clearAuthTokens }
